import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  PurchaseOrder, CreatePurchaseOrderInput, ListPurchaseOrdersFilters, PurchaseOrderStatus, Supplier,
  ReceiveLineInput,
  listPurchaseOrders, getPurchaseOrder, createPurchaseOrder, updatePurchaseOrderStatus,
  approvePurchaseOrder, receivePurchaseOrder, listSuppliers,
} from '../services/purchaseOrdersApi';

interface POState {
  items: PurchaseOrder[];
  current: PurchaseOrder | null;
  suppliers: Supplier[];
  pagination: { total: number; page: number; limit: number; pages: number } | null;
  filters: ListPurchaseOrdersFilters;
  loading: boolean;
  saving: boolean;
  error: string | null;
  setFilters: (patch: Partial<ListPurchaseOrdersFilters>) => void;
  fetchList: () => Promise<void>;
  fetchOne: (id: number | string) => Promise<PurchaseOrder>;
  fetchSuppliers: () => Promise<void>;
  create: (input: CreatePurchaseOrderInput) => Promise<PurchaseOrder>;
  setStatus: (id: number | string, status: PurchaseOrderStatus) => Promise<PurchaseOrder>;
  approve: (id: number | string) => Promise<PurchaseOrder>;
  receive: (id: number | string, lines: ReceiveLineInput[]) => Promise<PurchaseOrder>;
}

const defaultFilters: ListPurchaseOrdersFilters = { page: 1, limit: 20 };
const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const usePurchaseOrdersStore = create<POState>()(
  devtools(
    (set, get) => ({
      items: [],
      current: null,
      suppliers: [],
      pagination: null,
      filters: { ...defaultFilters },
      loading: false,
      saving: false,
      error: null,
      setFilters: (patch) => set({ filters: { ...get().filters, ...patch, page: 1 } }),
      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const result = await listPurchaseOrders(get().filters);
          set({ items: result.data, pagination: result.pagination, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },
      fetchOne: async (id) => {
        set({ loading: true, error: null });
        try {
          const po = await getPurchaseOrder(id);
          set({ current: po, loading: false });
          return po;
        } catch (err) {
          set({ loading: false, error: toErr(err) });
          throw err;
        }
      },
      fetchSuppliers: async () => {
        try {
          const data = await listSuppliers();
          set({ suppliers: data });
        } catch (err) {
          set({ error: toErr(err) });
        }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createPurchaseOrder(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      setStatus: async (id, status) => {
        set({ saving: true, error: null });
        try {
          const updated = await updatePurchaseOrderStatus(id, status);
          set((s) => ({
            items: s.items.map((it) => (String(it.id) === String(id) ? updated : it)),
            current: s.current && String(s.current.id) === String(id) ? updated : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      approve: async (id) => {
        set({ saving: true, error: null });
        try {
          const updated = await approvePurchaseOrder(id);
          set((s) => ({
            items: s.items.map((it) => (String(it.id) === String(id) ? updated : it)),
            current: s.current && String(s.current.id) === String(id) ? updated : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      receive: async (id, lines) => {
        set({ saving: true, error: null });
        try {
          const updated = await receivePurchaseOrder(id, lines);
          set((s) => ({
            items: s.items.map((it) => (String(it.id) === String(id) ? updated : it)),
            current: s.current && String(s.current.id) === String(id) ? updated : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
    }),
    { name: 'purchase-orders-store' }
  )
);
